import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import {
  HUB_CHILDREN,
  NAV_DESTINATIONS,
  normalisePath,
  parentFor,
  roleCanOpen,
  shellOf,
} from '@/lib/parentFor';

/**
 * Back returns to where you came from (wave w1024, 2026-10-24; design
 * grammar §4.2 / §4.6, which this reverses).
 *
 * THE ORIGIN. Every history entry React Router creates carries a `key`
 * (kept in `history.state`, so a reload keeps it; a new tab or a deep link
 * starts with none). When the reader moves from one page to another, the new
 * entry's origin is the page they left — its pathname AND its query, so a
 * filtered list comes back filtered — and the key of the entry they left, so
 * its scroll position can come back too. Origins live in sessionStorage,
 * keyed by entry key, bounded to the last `MAX_ENTRIES`.
 *
 * `BackLink` reads the current entry's origin and links to it with a real
 * `to=` (never `navigate(-1)`, which can leave the app: §4.6). Where there is
 * no usable origin it falls back to the role-aware canonical parent
 * (`parentFor`), carrying the query that list was last left with.
 *
 * The rules for when an entry gets an origin (`nextOrigin`):
 *   - PUSH to a different page in the same shell → the page left.
 *   - PUSH to the same pathname (a tab or filter pushed as a new entry), and
 *     every REPLACE (a redirect, a `replace: true` filter, a wizard step) →
 *     the entry inherits the origin of the one it follows or replaces. A
 *     replaced entry no longer exists in history, so it cannot be "where you
 *     came from", and a redirect hop must never become a back target.
 *   - A PUSH made BY a back link (`state.trfBackFrom`) → the origin of the
 *     entry it returns to, so A → B → back to A shows A's own back, not B.
 *   - POP (browser Back/Forward, reload) → nothing new: the entry keeps what
 *     it was given when it was created.
 *   - Never across shells (staff ↔ family) and never from an `outside` page
 *     (sign-in, the public landing, the legal library, an unknown path).
 *   - Never for the first entry of a tab (`key === 'default'`): that is a
 *     deep link by definition, and the key is reused by every fresh load.
 */

/** Where an entry came from: the page (path + query) and that entry's key. */
export interface NavOrigin {
  /** `pathname + search` of the page the reader left. */
  path: string;
  /** The history key of that page's entry — its saved scroll lives here. */
  key: string;
}

/** How the entry was made: React Router's `NavigationType` values. */
export type NavKind = 'POP' | 'PUSH' | 'REPLACE';

/** The location shape the rules need. */
export interface NavLocation {
  key: string;
  pathname: string;
  search: string;
}

/** How many entries each sessionStorage map keeps (oldest dropped first). */
export const MAX_ENTRIES = 200;

/** The first entry of a fresh load. Never carries an origin (see above). */
const DEFAULT_KEY = 'default';

const ORIGINS = 'trf:nav:origins';
const SCROLL = 'trf:nav:scroll';
const LAST_QUERY = 'trf:nav:last-query';

// ── storage ──────────────────────────────────────────────────────────────
//
// sessionStorage can throw (private mode, blocked storage) or be absent; the
// memory map keeps the feature working for the life of the page in that case.

const memory = new Map<string, string>();

function readRaw(name: string): string | null {
  try {
    return window.sessionStorage.getItem(name);
  } catch {
    return memory.get(name) ?? null;
  }
}

function writeRaw(name: string, value: string): void {
  try {
    window.sessionStorage.setItem(name, value);
  } catch {
    memory.set(name, value);
  }
}

type Entries<T> = Array<[string, T]>;

function readEntries<T>(name: string): Entries<T> {
  const raw = readRaw(name);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Entries<T>) : [];
  } catch {
    return [];
  }
}

function getEntry<T>(name: string, key: string): T | undefined {
  return readEntries<T>(name).find(([k]) => k === key)?.[1];
}

function putEntry<T>(name: string, key: string, value: T, max = MAX_ENTRIES): void {
  const next = readEntries<T>(name).filter(([k]) => k !== key);
  next.push([key, value]);
  writeRaw(name, JSON.stringify(next.slice(-max)));
}

// ── change notification (for BackLink) ──────────────────────────────────
//
// The tracker records in a layout effect, AFTER the header's BackLink has
// rendered for the new entry. A version counter through useSyncExternalStore
// re-renders the BackLink synchronously, before paint, once the origin lands.

let version = 0;
const listeners = new Set<() => void>();

function emit(): void {
  version += 1;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-render when an origin or a remembered query changes. */
export function useNavigationOriginVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => version);
}

// ── origins ──────────────────────────────────────────────────────────────

export function getOrigin(key: string | undefined): NavOrigin | null {
  if (!key || key === DEFAULT_KEY) return null;
  const o = getEntry<NavOrigin>(ORIGINS, key);
  return o && typeof o.path === 'string' && typeof o.key === 'string' ? o : null;
}

export function setOrigin(key: string, origin: NavOrigin): void {
  if (!key || key === DEFAULT_KEY) return;
  putEntry(ORIGINS, key, origin);
  emit();
}

/** The two shells may share an origin: same shell, or one side neutral. */
function sameShell(a: string, b: string): boolean {
  const sa = shellOf(a);
  const sb = shellOf(b);
  if (sa === 'outside' || sb === 'outside') return false;
  return sa === sb || sa === 'neutral' || sb === 'neutral';
}

/**
 * The origin a NEW entry should carry, or `null` for none. Pure; the rules
 * are the module comment's.
 */
export function nextOrigin(args: {
  prev: NavLocation | null;
  next: NavLocation;
  navigationType: NavKind;
  /** `state.trfBackFrom` on the new entry: the key a back link returned to. */
  backFrom?: string | null;
}): NavOrigin | null {
  const { prev, next, navigationType, backFrom } = args;
  if (navigationType === 'POP' || next.key === DEFAULT_KEY) return null;
  if (backFrom) return getOrigin(backFrom);
  if (!prev) return null;
  if (navigationType === 'REPLACE' || normalisePath(prev.pathname) === normalisePath(next.pathname)) {
    return getOrigin(prev.key);
  }
  if (!sameShell(prev.pathname, next.pathname)) return null;
  return { path: `${prev.pathname}${prev.search}`, key: prev.key };
}

/**
 * Record the new entry's origin, unless it already has one (a revisit keeps
 * the origin it was created with).
 */
export function recordNavigation(args: Parameters<typeof nextOrigin>[0]): void {
  if (getOrigin(args.next.key)) return;
  const origin = nextOrigin(args);
  if (origin) setOrigin(args.next.key, origin);
}

// ── last query per list ─────────────────────────────────────────────────

/** A path a fallback back link can land on: a sidebar destination or hub child. */
function isParentCandidate(path: string): boolean {
  return NAV_DESTINATIONS.has(path) || path in HUB_CHILDREN;
}

/** Remember the query a list page is showing, so the fallback can restore it. */
export function rememberQuery(pathname: string, search: string): void {
  const path = normalisePath(pathname);
  if (!isParentCandidate(path)) return;
  if ((getEntry<string>(LAST_QUERY, path) ?? '') === search) return;
  putEntry(LAST_QUERY, path, search);
  emit();
}

/** The query `pathname` was last left with (`?a=1`), or `''`. */
export function lastQueryFor(pathname: string): string {
  const q = getEntry<string>(LAST_QUERY, normalisePath(pathname));
  return typeof q === 'string' && (q === '' || q.startsWith('?')) ? q : '';
}

// ── scroll ───────────────────────────────────────────────────────────────

export function saveScroll(key: string, top: number): void {
  if (!key || !Number.isFinite(top)) return;
  putEntry(SCROLL, key, Math.max(0, Math.round(top)));
}

export function getScroll(key: string | null | undefined): number | null {
  if (!key) return null;
  const v = getEntry<number>(SCROLL, key);
  return typeof v === 'number' && v > 0 ? v : null;
}

/** How long a restore waits for the list to grow tall enough. */
export const RESTORE_TIMEOUT_MS = 1000;

const frame: (cb: () => void) => number =
  typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
    ? (cb) => window.requestAnimationFrame(cb)
    : (cb) => window.setTimeout(cb, 16) as unknown as number;
const cancelFrame: (id: number) => void =
  typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function'
    ? (id) => window.cancelAnimationFrame(id)
    : (id) => window.clearTimeout(id);

/**
 * Scroll `el` to `top` once its content is tall enough, retrying each frame
 * (the list may still be fetching), and give up after `RESTORE_TIMEOUT_MS`
 * by applying `top` as far as it goes. A wheel, touch or key from the reader
 * cancels it — their scroll wins. Returns a cancel function.
 */
export function restoreScroll(el: HTMLElement, top: number, timeoutMs = RESTORE_TIMEOUT_MS): () => void {
  const started = Date.now();
  let id = 0;
  let done = false;
  let applied = false;
  const stop = () => {
    done = true;
    cancelFrame(id);
    el.removeEventListener('wheel', stop);
    el.removeEventListener('touchstart', stop);
    el.removeEventListener('keydown', stop);
  };
  el.addEventListener('wheel', stop, { passive: true });
  el.addEventListener('touchstart', stop, { passive: true });
  el.addEventListener('keydown', stop);
  const tick = () => {
    if (done) return;
    const room = el.scrollHeight - el.clientHeight;
    if (room >= top) {
      // Settled once a frame after applying still reads the target — the
      // route-change ScrollToTop runs in a passive effect and may land after
      // the first application.
      if (applied && Math.abs(el.scrollTop - top) <= 1) return stop();
      el.scrollTop = top;
      applied = true;
    } else if (Date.now() - started >= timeoutMs) {
      el.scrollTop = top;
      return stop();
    }
    id = frame(tick);
  };
  id = frame(tick);
  return stop;
}

/** The state a back link puts on the entry it creates. */
export interface BackLinkState {
  trfBackFrom: string;
}

function backFromOf(state: unknown): string | null {
  const v = (state as Partial<BackLinkState> | null)?.trfBackFrom;
  return typeof v === 'string' && v ? v : null;
}

// ── the hook, wired once in MainLayout ──────────────────────────────────

/**
 * Track origins, remember list queries, and save/restore the scroll of
 * `scrollRef` (MainLayout's `<main>`, the shell's scroll container) per
 * history entry. Restores on POP (browser Back/Forward) and on a back-link
 * return; every other navigation is fresh and starts at the top, which
 * `ScrollToTop` already does on a pathname change.
 */
export function useNavigationOrigin(scrollRef: RefObject<HTMLElement | null>): void {
  const location = useLocation();
  const navigationType = useNavigationType() as NavKind;
  const prevRef = useRef<NavLocation | null>(null);
  const topRef = useRef(0);
  const cancelRestore = useRef<(() => void) | null>(null);

  // The live scroll position of the current entry. Read from scroll events,
  // not from the element at navigation time: by then the new route has
  // rendered and the old offset may already be clamped to the new height.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      topRef.current = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  useEffect(() => () => cancelRestore.current?.(), []);

  useLayoutEffect(() => {
    const prev = prevRef.current;
    const next: NavLocation = { key: location.key, pathname: location.pathname, search: location.search };
    // StrictMode re-runs the effect for the same entry; record once.
    if (prev && prev.key === next.key) return;

    cancelRestore.current?.();
    cancelRestore.current = null;
    if (prev) saveScroll(prev.key, topRef.current);

    const backFrom = backFromOf(location.state);
    recordNavigation({ prev, next, navigationType, backFrom });
    rememberQuery(next.pathname, next.search);

    const el = scrollRef.current;
    const samePage = prev !== null && normalisePath(prev.pathname) === normalisePath(next.pathname);
    topRef.current = samePage && el ? el.scrollTop : 0;
    prevRef.current = next;

    // The first entry this layout sees is a load or a reload: leave it be.
    if (!prev || !el) return;
    const restoreKey = navigationType === 'POP' ? next.key : backFrom;
    const top = getScroll(restoreKey);
    if (top !== null) {
      topRef.current = top;
      cancelRestore.current = restoreScroll(el, top);
    }
    // Keyed on the entry: every navigation, including a same-path one, is a
    // new key; the rest of `location` is read from the same render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
}

// ── the back target ─────────────────────────────────────────────────────

/** Where a back link goes, and what it says. */
export interface BackTarget {
  /** The link's `to=`: path plus query. */
  to: string;
  /** The destination's nav name, or `null` for a page without one ("← Back"). */
  label: string | null;
  /** On an origin target, the entry key to restore scroll from. */
  backFrom: string | null;
}

/**
 * Resolve the back link for the entry `key` on `pathname`.
 *
 * Presence is unchanged from the canonical rule: where `parentFor` gives no
 * parent (a sidebar destination, a page that owns its wayfinding, outside
 * every shell) there is no back link, origin or not — depth 0 never grows a
 * trail (§4.2: no breadcrumbs, depth two).
 *
 * Otherwise:
 *   1. the in-app origin, when it exists, is a different page from both the
 *      current path and `resolvePath`, and the reader's role can open it →
 *      the origin, with its query, labelled by its nav name (or "Back");
 *   2. else the role-aware canonical parent, with the query that list was
 *      last left with.
 */
export function backTarget(args: {
  /** The entry key (`location.key`). */
  key: string | undefined;
  /** The current pathname. */
  pathname: string;
  /** The path the canonical parent resolves from, when it differs (the header's `/chat`). */
  resolvePath?: string;
  /** The reader's active role, when known. */
  role?: string;
}): BackTarget | null {
  const { key, pathname, role } = args;
  const resolvePath = args.resolvePath ?? pathname;
  const parent = parentFor(resolvePath, { role });
  if (!parent) return null;

  const origin = getOrigin(key);
  if (origin) {
    const originPath = normalisePath(origin.path);
    const usable =
      originPath !== normalisePath(pathname)
      && originPath !== normalisePath(resolvePath)
      && shellOf(originPath) !== 'outside'
      && (role === undefined || roleCanOpen(originPath, role));
    if (usable) {
      return { to: origin.path, label: NAV_DESTINATIONS.get(originPath) ?? null, backFrom: origin.key };
    }
  }

  return { to: `${parent.path}${lastQueryFor(parent.path)}`, label: parent.label, backFrom: null };
}
